import { STORAGE_KEYS } from "@/lib/storage-keys";

interface PersistPRFCredentialOptions {
	credentialId: string;
	prfSalt: string;
	encryptedBackup: string;
	deviceName?: string;
}

interface HydratePRFCredentialOptions {
	/**
	 * When true, avoid replacing an existing local PRF entry. Issue #93 will
	 * handle multi-passkey slots; for now the server intentionally has one row.
	 */
	skipIfPresent?: boolean;
}

interface ServerPRFCredential {
	credentialId?: unknown;
	prfSalt?: unknown;
	encryptedBackup?: unknown;
}

function warnPRFPersistenceFailure(context: string, details?: unknown): void {
	console.warn(
		`PRF server persistence failed (${context}); local passkey unlock still works, but cross-device/post-wipe restore may be unavailable.`,
		details
	);
}

/**
 * Best-effort server persistence for the live Better Auth passkey PRF setup.
 * Never throws: localStorage remains the source of immediate unlock capability.
 */
export async function persistPRFCredentialServerSide({
	credentialId,
	prfSalt,
	encryptedBackup,
	deviceName,
}: PersistPRFCredentialOptions): Promise<void> {
	try {
		const enrollRes = await fetch("/api/webauthn/prf/enroll", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ credentialId, prfSalt, deviceName }),
		});
		if (!enrollRes.ok) {
			warnPRFPersistenceFailure("enroll non-2xx", {
				status: enrollRes.status,
			});
			return;
		}

		const backupRes = await fetch("/api/webauthn/prf/credential", {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ encryptedBackup }),
		});
		if (!backupRes.ok) {
			warnPRFPersistenceFailure("backup PUT non-2xx", {
				status: backupRes.status,
			});
		}
	} catch (err) {
		warnPRFPersistenceFailure("network/error", err);
	}
}

/**
 * Restore the authenticated user's server-backed PRF metadata into localStorage.
 *
 * The route requires a Better Auth session. On /login that means a completely
 * signed-out visitor gets a silent 401 and we do not weaken auth by exposing
 * ciphertext through an unauthenticated credential lookup.
 */
export async function hydratePRFCredentialFromServer({
	skipIfPresent = true,
}: HydratePRFCredentialOptions = {}): Promise<boolean> {
	if (typeof window === "undefined") {
		return false;
	}
	if (skipIfPresent && localStorage.getItem(STORAGE_KEYS.PRF_CREDENTIAL_ID)) {
		return false;
	}

	try {
		const res = await fetch("/api/webauthn/prf/credential", {
			credentials: "include",
		});
		if (res.status === 401) {
			return false;
		}
		if (!res.ok) {
			console.warn(
				"PRF credential hydration failed; biometric unlock cannot be restored from server.",
				{ status: res.status }
			);
			return false;
		}

		const { credential } = (await res.json()) as {
			credential?: ServerPRFCredential | null;
		};
		if (
			!(
				typeof credential?.credentialId === "string" &&
				typeof credential.prfSalt === "string" &&
				typeof credential.encryptedBackup === "string"
			)
		) {
			return false;
		}

		localStorage.setItem(
			STORAGE_KEYS.PRF_CREDENTIAL_ID,
			credential.credentialId
		);
		localStorage.setItem(STORAGE_KEYS.PRF_SALT, credential.prfSalt);
		localStorage.setItem(
			STORAGE_KEYS.PRF_ENCRYPTED_BACKUP,
			credential.encryptedBackup
		);
		window.dispatchEvent(new Event("prf-credential-hydrated"));
		return true;
	} catch (err) {
		console.warn(
			"PRF credential hydration errored; falling back to password unlock.",
			err
		);
		return false;
	}
}
